import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import type {
  DoczFragment,
  DoczHandle,
  StructuralLossItem,
} from "@norkron/web-docz";
import {
  type CanvasEditorHandle,
  canvasDoczBlocks,
  createCanvasEditor,
  createWasmRelayout,
  type EditableBlock,
  type EditBridge,
  type EditorCore,
  handleFromBridge,
  isCanvasAddressableKind,
  loadPageLayout,
  makeStructuredEncodeBlocks,
  type PageRect,
} from "@norkron/web-page-layout";
import {
  type ActiveFormat,
  type AnchorHit,
  type AnchorHitState,
  type AnchorSelector,
  type BlockPatch,
  canvasHasSelection,
  type ElaborateEditorHandle,
  emptySectionDecoration,
  mergeSectionDecoration,
  type SectionDecoration,
  type SectionDecorationPatch,
  type SystemPasteResult,
  type TableEditVerbs,
  type ToggleResult,
} from "@norkron/web-elaborate-editor";
import {
  CANVAS_CAPABILITIES,
  type CanvasCapabilities,
} from "./canvasCapabilities";
import { CANVAS_EDIT_PAGE_CONFIG } from "./canvasPageConfig";
import { pageConfigWithMargins } from "./canvasPageMargins";
import {
  applyCanvasScale,
  DOCUMENT_BLOCK_MARGIN_CSS,
} from "./canvasPresentation";
import {
  type CanvasFormatLayer,
  createCanvasFormatLayer,
} from "./canvasFormatLayer";
import {
  type CanvasEditorServices,
  createCanvasEditorServices,
} from "./canvasEditorServices";
import {
  anchorRefusal,
  bandsFromSelector,
  bandsOfSelector,
  type CommentBand,
  relocateBands,
  selectorForSlices,
} from "./canvasCommentBands";
import { type CanvasInsertLayer, createCanvasInsertLayer } from "./canvasInsertLayer";
import { type CanvasListLayer, createCanvasListLayer } from "./canvasListLayer";
import { type CanvasTableLayer, createCanvasTableLayer } from "./canvasTableLayer";
import {
  FormatBridge,
  type OverrideMap,
  overridesFor,
  syncOverrides,
} from "./canvasFormatBridge";
import type { AnchorableSurface } from "../../lib/surfaceComment";
import { useDevicePixelRatio } from "../../lib/useDevicePixelRatio";

/**
 * `<CanvasElaborateEditor>` — the C1 foundation slice: a DROP-IN replacement for
 * `@norkron/web-elaborate-editor`'s DOM `<ElaborateEditor>` that edits on the
 * paginated CANVAS engine (`@norkron/web-page-layout`) instead of a
 * contenteditable, while exposing the EXACT SAME {@link ElaborateEditorHandle}
 * the ribbon (`EditorToolbarMount`) and the workbench Save path already consume.
 *
 * Because the ref shape is identical, the workbench's `editorRef.current.toHtml()`
 * Save call is UNCHANGED — it now serializes the Canvas's live docz handle. Edits
 * mutate the ONE real docz IR through the upstream, mark-preserving
 * {@link DoczEditBridge} (comment/format marks split/shift in `docz.wasm`), so a
 * pre-existing comment on an untouched block survives edit → save → reload.
 *
 * HONEST SCOPE — the surface declares it, the surface does not hide it. Typing /
 * relayout / repaint, inline formatting (C2), page decoration (Bundle 2), comment
 * anchors (C3), TABLES (the contextual ribbon group and all 16 cell verbs) and
 * STRUCTURAL INSERTS (a real table or horizontal rule, caret-addressable and
 * save-surviving), plus the `toHtml()` save round-trip, are REAL. Images, page
 * breaks, lists, the content-model insert menu, proposals and validation decline —
 * each for a reason MEASURED against the shipped wasm and recorded beside its
 * group in `canvasCapabilities.ts` (never a fabricated success). The ribbon IS mounted over
 * this surface, and its enable gate reads the loaded `docz.wasm` export table —
 * which exports every probed symbol — so the stubs would otherwise render as
 * fully-enabled, silently-dead buttons. {@link CANVAS_CAPABILITIES} is this
 * surface's own capability statement, published on the handle as `capabilities`
 * for the toolbar env to intersect with the wasm-derived `FormatSupports`: a
 * surface can only ever SUBTRACT a capability, never fake one. `canvasCapabilities.ts`
 * is the single source of truth; `canvasCapabilities.test.ts` fails CI if a new
 * stub is added without declaring it.
 *
 * Reachable in TWO ways (the `?canvasEdit=1` flag is NOT the only door): the
 * user-facing "Paginated · Canvas" segmented control in `<ResourceHeader>` mounts
 * it for anyone, and the flag force-selects it for dev/E2E.
 */

// ── honest safe-default constants (never a fabricated success) ────────────────

/** Empty active-format snapshot — no marks, inherit paragraph. TODO(C2): read the
 *  real per-run marks off the caret's docz fragment. */
const EMPTY_ACTIVE_FORMAT: ActiveFormat = {
  marks: new Set(),
  link: null,
  color: null,
  highlight: null,
  fontFamily: null,
  fontSize: null,
  underlineStyle: null,
  letterSpacing: null,
  // FEATURE BUNDLE 3 (W2/W3) — no char-decoration active in the empty format.
  textEffects: null,
  charBorder: null,
  emphasis: null,
  kind: "paragraph",
  level: null,
  para: {},
};

/** No-op toggle result (nothing applied). TODO(C2). */
const NOOP_TOGGLE: ToggleResult = { fragment: null, applied: false, toggledOff: false };

/**
 * Table verbs for a surface with no live editor yet (before mount / after
 * dispose). NOT the shipped table model — that is {@link createCanvasTableLayer},
 * which drives the real `dz_table_*` verbs in `docz.wasm` against the caret's own
 * cell. This exists only so the handle is total before the first layout lands.
 */
const NO_EDITOR_TABLE: TableEditVerbs = {
  selectTable: () => false,
  deleteTable: () => false,
  insertRow: () => false,
  deleteRow: () => false,
  insertColumn: () => false,
  deleteColumn: () => false,
  mergeCells: () => false,
  splitCell: () => false,
  setCellShading: () => false,
  setCellVAlign: () => false,
  setCellBorders: () => false,
  setCellAlignH: () => false,
  toggleHeader: () => false,
  setColumnWidth: () => false,
  setRowHeadColumns: () => false,
  applyTableStyle: () => false,
  setHeaderStyle: () => false,
  setRowBanding: () => false,
};

export interface CanvasElaborateEditorProps {
  /** The live docz doc the Canvas edits — the SAME handle the DOM editor takes. */
  readonly doc: DoczHandle;
  /** Fired with each edit (dirty signal — the workbench bumps its tick/dirty). A
   *  lightweight block-scoped patch (the focused block id); the char-level delta is
   *  not surfaced in the foundation slice. TODO(C2). */
  readonly onChange?: (patch: BlockPatch) => void;
  /** Fired with the live handle after each edit (so a host that wants the IR — not
   *  just `toHtml()` — stays current). */
  readonly onDocChange?: (handle: DoczHandle) => void;
  /** Extra className on the canvas surface root. */
  readonly className?: string;
  /** `data-testid` on the surface (defaults to `canvas-elaborate-editor`). */
  readonly testId?: string;
  /** Initial zoom (1 = 100%). */
  readonly zoom?: number;
  /**
   * A gesture the surface REFUSED, in words the reviewer can read.
   *
   * Wired to a toast by `<DoczCanvas>`. It exists because on 2026-07-28 a click
   * inside a table cell reported NOTHING anywhere — no console, no toast, no
   * `role=alert`, no network — while the caret stayed off-screen on another page
   * and kept taking keystrokes. The engine now announces the refusal
   * (`onPointRefused`) and scrolls the caret back into view; this is the half that
   * makes it reach a human.
   */
  readonly onRefusal?: (message: string) => void;
}

type LoadState =
  | { readonly status: "loading" }
  | { readonly status: "error"; readonly message: string }
  | { readonly status: "ready" };

/** Add an alpha channel to a computed `rgb(r, g, b)` string. */
function withAlpha(rgb: string, alpha: number): string {
  const m = rgb.match(/(\d+(?:\.\d+)?)/g);
  if (!m || m.length < 3) return rgb;
  return `rgba(${m[0]}, ${m[1]}, ${m[2]}, ${alpha})`;
}

/** Resolve caret + selection colours from theme tokens into CONCRETE strings — the
 *  Canvas 2D `fillStyle` cannot parse CSS `var(--x)` (silently falls to black). */
function resolveOverlayColors(host: HTMLElement): { caret: string; selection: string } {
  const probe = host.ownerDocument.createElement("span");
  probe.style.position = "absolute";
  probe.style.visibility = "hidden";
  host.appendChild(probe);
  const read = (expr: string, fallback: string): string => {
    probe.style.color = "";
    probe.style.color = expr;
    const c = getComputedStyle(probe).color;
    return c ? c : fallback;
  };
  const caretRgb = read("var(--norkron-text-primary)", "rgb(20, 20, 20)");
  const accentRgb = read("var(--norkron-accent)", "rgb(37, 99, 235)");
  probe.remove();
  return { caret: caretRgb, selection: withAlpha(accentRgb, 0.3) };
}

/**
 * The canvas editor's handle: the shared {@link ElaborateEditorHandle} contract,
 * plus the surface's own capability statement, plus the SAVE REFUSAL PREDICATE.
 *
 * `structuralLossOnSave()` is declared HERE rather than upstream because it is a
 * property of a serializing surface, and elaborate is the only host that posts
 * `rendered_html`. A host reads it right before it writes; a non-empty result
 * means the write would destroy structure the document still holds, and the host
 * must NOT post. See `canvasFormatLayer.structuralLossOnSave`.
 */
export type SavingEditorHandle = ElaborateEditorHandle & {
  /** Optional on the shared contract because only a SERIALIZING surface can
   *  answer it; absent means "no measurement available", never "safe". */
  structuralLossOnSave?(): readonly StructuralLossItem[];
};

export type GuardedElaborateEditorHandle = SavingEditorHandle &
  AnchorableSurface & {
    readonly capabilities: CanvasCapabilities;
    structuralLossOnSave(): readonly StructuralLossItem[];
  };

export const CanvasElaborateEditor = forwardRef<
  GuardedElaborateEditorHandle,
  CanvasElaborateEditorProps
>(function CanvasElaborateEditor(props, ref) {
  const {
    doc,
    onChange,
    onDocChange,
    className,
    testId = "canvas-elaborate-editor",
    zoom = 1,
    onRefusal,
  } = props;
  // Live, capped display DPR — drives the canvas backing-store scale so text stays
  // crisp on Retina (2x) AND re-scales when the window moves between displays or
  // the browser zoom changes (the mount effect re-runs on the new ratio below).
  const dpr = useDevicePixelRatio(2);
  const mountRef = useRef<HTMLDivElement>(null);
  const probeRef = useRef<HTMLSpanElement>(null);
  const editorRef = useRef<CanvasEditorHandle | null>(null);
  // ── A `focus()` THAT ARRIVED BEFORE THE SURFACE EXISTED ─────────────────────
  // The imperative handle is published by `useImperativeHandle` on the FIRST
  // commit, but the surface it delegates to is built in an ASYNC effect (two wasm
  // modules load first). So there is a real window — the whole of it, on a cold
  // load — in which `handle.focus()` resolves to `editorRef.current?.focus()`
  // with `editorRef.current === null` and silently does NOTHING.
  //
  // That window is exactly when the host asks. Entering edit mode is a mount, and
  // the host's "the editor is now the keyboard target" call lands on the tick the
  // handle appears, not the tick the wasm finishes. Dropping the request there is
  // half of the P0 this file is being changed for: focus wiring that is correct
  // and unobservable because it fired into a surface that was not there yet.
  //
  // So the request LATCHES, and the ready path below spends it. One shot, cleared
  // when spent and when a `focus()` finds a live surface, so it can never re-fire
  // on a later relayout/remount that nobody asked to be focused.
  const pendingFocusRef = useRef(false);
  // The refusal reporter, read through a ref: the surface is created ONCE in an
  // effect, so capturing the prop directly would pin the first render's callback
  // and a later `onRefusal` would never be called.
  const onRefusalRef = useRef<((message: string) => void) | undefined>(undefined);
  onRefusalRef.current = onRefusal;
  // The live docz handle after the latest Canvas edit — the Save witness.
  const liveHandleRef = useRef<DoczHandle>(doc);
  // C2 mark model: per-block styled-run overrides (the transport DoczBlock is
  // plain-text + comments only). Paints + saves the inline marks. See
  // `canvasFormatLayer` — this is the SAME shape the DOM controller keeps.
  const overridesRef = useRef<Map<string, DoczFragment>>(new Map());
  // FEATURE BUNDLE 2 — the live SECTION/PAGE decoration (page color / borders /
  // line numbering). The relayout closure reads it per frame via the getter below,
  // so `setPageDecoration` → `editor.refresh()` re-frames the PLCF V2 header and the
  // canvas repaints the page fill / border box / line-number gutter.
  const sectionDecorationRef = useRef<SectionDecoration>(emptySectionDecoration());
  // P3-1 — the CONTINUOUS column's live measure, in points, derived from the
  // mount's CSS width. `null` until the mount has been measured (then the paper
  // width stands in). The relayout config getter reads this every frame, so a
  // viewport resize reflows without a remount and without losing the caret.
  // C3 comment-anchor model: the durable selector geometry per comment
  // (blockId + block-relative UTF-8 byte range + the exact quote). The paint
  // source for the highlight band + the resolve/rebase input. Byte offsets are
  // re-located against the live block text on every repaint so the band tracks
  // reflow (and a shifted anchor re-homes) — see {@link collectHighlightRects}.
  //
  // ⚠ ONE COMMENT, N BANDS. A comment anchored across blocks paints one band PER
  // BLOCK — a range that crosses a paragraph boundary has no single (blockId,
  // start, end) that could describe it, so storing one per comment made every
  // multi-block comment either unpaintable or wrong. See `canvasCommentBands.ts`.
  const commentAnchorsRef = useRef<Map<string, readonly CommentBand[]>>(new Map());
  const [load, setLoad] = useState<LoadState>({ status: "loading" });

  // Recompute the union of comment-highlight rects off the CURRENT geometry: for
  // each stored anchor, re-locate its quote in the live block text (exact at the
  // stored offsets, or shifted to where it now sits), then ask the engine for the
  // range's page-space rects. Orphaned anchors (quote gone) contribute nothing.
  const collectHighlightRects = useCallback((): PageRect[] => {
    const core = editorRef.current?.core;
    if (!core) return [];
    // THE COORDINATE: a comment band is painted at a `blockOrd`, and a `blockOrd`
    // indexes the CANVAS DOCUMENT VIEW — the block list the layout pass encoded —
    // not `handle.blocks`. They diverge for any document carrying an opaque
    // `kind:'html'` island (every list / quote / code block the server sends), so
    // resolving the anchor against the handle painted the band on a different
    // block. See `canvasDoczBlocks` upstream.
    const view = canvasDoczBlocks(core.getState().bridge) ?? liveHandleRef.current.blocks;
    const rects: PageRect[] = [];
    for (const bands of commentAnchorsRef.current.values()) {
      for (const r of relocateBands(view, bands)) {
        rects.push(
          ...core.rangeRects(
            { blockOrd: r.blockOrd, byteOffset: r.start },
            { blockOrd: r.blockOrd, byteOffset: r.end },
          ),
        );
      }
    }
    return rects;
  }, []);

  /** THE COORDINATE every `blockOrd` in this component indexes: the CANVAS
   *  DOCUMENT VIEW (the block list the layout pass encoded), never
   *  `handle.blocks` — the two diverge for any opaque `kind:'html'` island. */
  const canvasView = useCallback(() => {
    const core = editorRef.current?.core;
    if (!core) return liveHandleRef.current.blocks;
    return canvasDoczBlocks(core.getState().bridge) ?? liveHandleRef.current.blocks;
  }, []);

  /**
   * The durable selector for the LIVE canvas selection — the ONE derivation, used
   * by `buildSelector`, `anchorRefusal` and `placeCommentMark` alike.
   *
   * ⛔ IT USED TO OPEN WITH `if (anchor.blockOrd !== focus.blockOrd) return null`.
   * That single line was the owner-reported P0: drag across a paragraph boundary
   * and the surface could not NAME the range, so "New comment" refused, silently,
   * for every selection bigger than one block. `selectionSlices()` is the upstream
   * decomposition of a range into its per-block sub-ranges, and
   * `buildSelectorFromSlices` is `buildSelectorFromOffsets` generalised — with one
   * slice it returns a byte-identical selector (pinned upstream), so there is
   * exactly ONE anchoring path here and the single-block case cannot drift from
   * the cross-block one.
   */
  const selectorNow = useCallback((): AnchorSelector | null => {
    const core = editorRef.current?.core;
    if (!core) return null;
    return selectorForSlices(
      canvasView(),
      core.selectionSlices(),
      liveHandleRef.current.baseVersionId,
    );
  }, [canvasView]);

  // The format layer (toolbar verbs + activeFormat + mark-aware toHtml), built
  // once over stable refs. Reads the live editor/handle on every call.
  const formatLayerRef = useRef<CanvasFormatLayer | null>(null);
  if (!formatLayerRef.current) {
    formatLayerRef.current = createCanvasFormatLayer({
      getEditor: () => editorRef.current,
      getHandle: () => liveHandleRef.current,
      overrides: overridesRef.current,
      getRoot: () => mountRef.current,
    });
  }

  // History / keyboard / clipboard / format painter for the PAGINATED surface —
  // pure composition of the shipped lib primitives (the shared snapshot ring, the
  // ONE command-catalog dispatcher, the web-docz clipboard codec, the painter).
  // Before this the canvas had none of them: its input sink mapped caret
  // navigation only, so ⌘Z / ⌘B / ⌘C did nothing here at all.
  // TABLES + STRUCTURAL INSERTS. Both are pure composition of the shipped
  // primitives: `canvasCellAt`/`canvasCellRegion` (web-page-layout) resolve the
  // caret to a cell, `applyTableVerb` (web-docz) runs the grid math inside
  // `docz.wasm`, and the produced document is installed through
  // `EditorCore.applyBridge` as ONE undoable step. Built over stable refs so the
  // handle below never re-binds.
  const tableLayerRef = useRef<CanvasTableLayer | null>(null);
  if (!tableLayerRef.current) {
    tableLayerRef.current = createCanvasTableLayer({
      getEditor: () => editorRef.current,
    });
  }
  // The LISTS layer. Same shape as the table layer and for the same reason: the
  // ribbon's Lists group is a projection of `docz.wasm`'s six list verbs over the
  // canvas selection, folded BLOCK-scoped through web-docz's `applyAcrossBlocks`
  // so a three-paragraph selection converts three blocks and not just the focus.
  const listLayerRef = useRef<CanvasListLayer | null>(null);
  if (!listLayerRef.current) {
    listLayerRef.current = createCanvasListLayer({
      getEditor: () => editorRef.current,
      // The SAME channel the insert layer and the hit-test refusal use — rendered as
      // a toast by `<DoczCanvas>`. Every list verb builds a `declined[].reason`
      // sentence and every one of them used to be thrown away, so a Lists command the
      // surface could not honour reached the reviewer as an inert button. A silent
      // refusal is indistinguishable from a broken feature, which is exactly how this
      // group spent its life.
      refuse: (message) => onRefusalRef.current?.(message),
    });
  }
  const insertLayerRef = useRef<CanvasInsertLayer | null>(null);
  if (!insertLayerRef.current) {
    insertLayerRef.current = createCanvasInsertLayer({
      getEditor: () => editorRef.current,
      // The SAME channel the hit-test refusal uses — `<DoczCanvas>` renders it as
      // a toast. A structural insert the surface cannot honour must reach a human;
      // a silent `false` is the failure class this whole change exists to remove.
      refuse: (message) => onRefusalRef.current?.(message),
    });
  }

  const servicesRef = useRef<CanvasEditorServices | null>(null);
  if (!servicesRef.current) {
    servicesRef.current = createCanvasEditorServices({
      getEditor: () => editorRef.current,
      getHandle: () => liveHandleRef.current,
      setHandle: (next) => {
        liveHandleRef.current = next;
      },
      getLayer: () => formatLayerRef.current,
      // ONE enable environment for one surface: the shortcut dispatcher asks the
      // SAME table layer the ribbon does, instead of hardcoding `inTable: false`.
      getTable: () => tableLayerRef.current,
    });
  }

  // Keep callbacks current without re-running the mount effect (which would tear
  // down the surface + lose the caret on every parent render).
  const cbRef = useRef({ onChange, onDocChange });
  cbRef.current = { onChange, onDocChange };

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    let disposed = false;
    let editor: CanvasEditorHandle | null = null;
    liveHandleRef.current = doc;
    // Fresh document → drop any stale marks + comment anchors from a prior doc.
    overridesRef.current.clear();
    commentAnchorsRef.current.clear();
    // The last bridge `publish` reported upward. Scoped to this mount effect, so a
    // remount (new document) starts from `null` and the seeding `publish` after
    // `createCanvasEditor` always counts as a change.
    let publishedBridge: unknown = null;

    /** `seed` = the first publish after the surface is built. It must do all the
     *  DOM work (probe, highlight bands, live handle) but must NOT report a
     *  document change: merely OPENING the editor had been marking an untouched
     *  document dirty, so Save was enabled before the reviewer touched anything. */
    const publish = (core: EditorCore, seed = false): void => {
      const bridge = core.getState().bridge;
      // ── A CARET MOVE IS NOT A DOCUMENT CHANGE ──────────────────────────────
      // `EditorCore.emit()` fires for BOTH kinds of state move: a mutation
      // (`applyIntent`) and a pure navigation (`setFocus`, i.e. every click and
      // every step of a drag-select). `publish` used to report both to the host
      // as `onChange`, and that one conflation was the measured cause of the
      // selection lag AND of a real correctness bug:
      //
      //   · `DoczCanvas.handleEditorChange` bumps `changeTick` → the whole
      //     92-command ribbon reconciles, once per pointer event;
      //   · `ArticleWorkbench.onEditorChange` then sets the pane DIRTY and bumps
      //     the word-count tick → the ENTIRE workbench screen re-renders, once
      //     per pointer event — and merely DRAGGING A SELECTION marked a document
      //     the reviewer never edited as having unsaved changes.
      //
      // Measured on the live paginated canvas, one 5.1 s drag: 122 pointer events
      // → 1123 ms of react-dom work, 63% of ALL main-thread time, against 65.5 ms
      // for the canvas's own selection painting.
      //
      // The gate is EXACT, not heuristic: `EditorCore` replaces `bridge` on every
      // mutation and leaves it strictly identical across `setFocus` (which touches
      // only `selection` / `goalXPt`). Identity of the bridge therefore IS
      // "the document did not change".
      const docChanged = !seed && bridge !== publishedBridge;
      publishedBridge = bridge;
      // UNDO COMPLETENESS. The snapshot `EditorCore` restores is `{bridge,
      // selection}`; the marks ride the bridge (see `canvasFormatBridge.ts`), so an
      // undo hands back a bridge whose override map is the PREVIOUS one. Re-seed the
      // live read map from it here, before anything reads `getFragment` — otherwise
      // the ribbon's `activeFormat()` would keep reporting the mark an undo just
      // removed. The PAINT does not depend on this sync: the relayout encoder reads
      // the overrides straight off the bridge it is handed.
      syncOverrides(overridesRef.current, bridge);
      const live = handleFromBridge(bridge);
      if (live) {
        liveHandleRef.current = live;
        // `onDocChange` republishes the live HANDLE (a fresh object every call).
        // `DoczCanvas` holds it in STATE, so publishing it on a caret move was a
        // second re-render of the same subtree for no new document. The SEED must
        // publish it — that is how the toolbar first binds to the surface.
        if (docChanged || seed) cbRef.current.onDocChange?.(live);
      }
      // Drop any override whose block text has diverged (typing) BEFORE reading the
      // probe / serializing — keeps paint + save honest (never a stale mark on
      // re-typed text).
      formatLayerRef.current?.reconcile();
      // Reflect the live IR plaintext into a hidden probe so an E2E can assert the
      // round-trip: what the Canvas typed IS in the docz blocks. Only the document
      // can move it, so a caret move need not touch the DOM.
      const probe = probeRef.current;
      if (probe && (docChanged || seed)) {
        probe.textContent = bridge.blocks.map((b) => b.text).join("\n");
        probe.dataset.blockCount = String(bridge.blocks.length);
      }
      // Re-locate + repaint the comment-anchor highlight bands off the new
      // geometry (byte offsets are stable across relayout; pixels are not).
      // Geometry only moves when the document does — a caret move relayouts
      // nothing, so the bands are already correct.
      if (docChanged || seed) editorRef.current?.setHighlights(collectHighlightRects());
      // Emit a lightweight block-scoped dirty patch (focused block id) — ONLY for
      // a real edit. This is what marks the pane dirty and enables Save.
      if (docChanged) {
        const focus = core.getState().selection.focus;
        const blockId = bridge.blocks[focus.blockOrd]?.id;
        if (blockId != null) cbRef.current.onChange?.({ blockId });
      }
      // The Canvas selection is INTERNAL (not a DOM selection); nudge any mounted
      // ribbon to re-read `activeFormat()` on every caret move / format op by
      // firing the same `selectionchange` tick its DOM-editor sibling listens for.
      // ALWAYS — this is the signal the toolbar's coalescer consumes, and it is
      // the only thing a pure caret move should cost.
      if (typeof document !== "undefined") {
        document.dispatchEvent(new Event("selectionchange"));
      }
    };

    void (async () => {
      try {
        const module = await loadPageLayout();
        if (disposed) return;
        // Mark-native encode: the STRUCTURED transport (`handle.toBlocks()`) is the
        // source of truth — bold / italic / underline / strike / colour / comment
        // marks ride it and reach `pl_layout` NATIVELY (italic → an oblique face,
        // underline/strike → the SoA v2 deco bits, bold → the bold face, colour →
        // the run colour — all now PAINTED by the renderer). A live session override
        // (a toolbar toggle not yet folded into the handle) OVERLAYS the matching
        // block; a stale override (text drifted) is ignored.
        //
        // The overrides the CURRENT encode pass must paint from. Read off the
        // BRIDGE the relayout was handed (`overridesFor`) rather than the live ref,
        // so an undo repaints the restored marks in the SAME pass instead of one
        // frame late; falls back to the live map for a bridge this app did not mint.
        let paintOverrides: OverrideMap = overridesRef.current;
        const encodeStructured = makeStructuredEncodeBlocks((b) =>
          b.id != null ? (paintOverrides.get(b.id) ?? null) : null,
        );
        const relayout = createWasmRelayout({
          module,
          encodeBlocks: (blocks: readonly EditableBlock[], bridge?: EditBridge) => {
            paintOverrides = overridesFor(bridge, overridesRef.current);
            return encodeStructured(blocks, bridge);
          },
          // FEATURE BUNDLE 2 — frame the live section decoration on EVERY relayout's
          // PLCF V2 header (read per frame, so a `setPageDecoration` toggle repaints
          // without recreating the relayout). The wasm SETS page_color/page_borders/
          // line_numbering on the docz.Page → the SoA carries the fill/border/gutter
          // records the renderer paints.
          sectionDecoration: () => sectionDecorationRef.current,
          // THE PAGE TEMPLATE. Omitting it is not "no opinion" — upstream resolves
          // an absent config to `A4_DEFAULT`, whose heading rhythm is all ZERO and
          // whose body leading is unset, so pdfz never calls `page.setSpaceNext`
          // and every line packs at the flex default 13pt. That silent fallback IS
          // the "everything is crushed together" the paginated view shipped with.
          // `CANVAS_EDIT_PAGE_CONFIG` is the Cerebr8 plate Body geometry the
          // branded preview and the exported PDF already use (review == export),
          // with `justify`/`glyphGranular` pinned on for caret provenance.
          //
          // P3-1 — a GETTER, not a value. `createWasmRelayout` used to capture the
          // config once, so a later width or mode change never reached the engine
          // and nothing reflowed. Read per frame, a `refresh()` after a resize
          // re-lays-out at the new measure with the caret's byte offsets intact.
          // PAGE MARGINS — the document's own margin box, overlaid on the plate
          // template. Read per frame from the SAME `sectionDecorationRef` the
          // decoration getter above reads, so `setPageMargins` re-lays-out
          // immediately without recreating the relayout. A document that has
          // never set margins yields `undefined` and the config passes through
          // byte-identically (`pageConfigWithMargins` is a no-op on absent).
          config: () =>
            pageConfigWithMargins(
              CANVAS_EDIT_PAGE_CONFIG,
              sectionDecorationRef.current.pageMargins,
            ),
        });
        editor = createCanvasEditor(mount, {
          // A `FormatBridge`, not a bare `DoczEditBridge`: it carries the session's
          // styled-run overrides, which is what puts them inside the `{bridge,
          // selection}` snapshot `EditorCore` restores on undo.
          bridge: new FormatBridge(doc),
          relayout,
          zoom,
          // Explicit backing-store DPR (RULE ZERO: the engine owns the transform;
          // the consumer only feeds the TRUE display ratio it can't infer itself).
          dpr,
          maxDpr: 2,
          // THE SAME block margin `<ReadCanvas>` passes (`canvasPresentation`).
          // This is the EDIT surface the workbench actually mounts, so it is the
          // one that has to agree with the reader: a document surface that skips
          // this sits 12px higher than the other, and the reviewer sees the page
          // jump the moment they press Edit.
          blockMarginCss: DOCUMENT_BLOCK_MARGIN_CSS,
          colors: resolveOverlayColors(mount),
          // The keyboard + clipboard seams. `onShortcut` runs AFTER the sink's nav
          // mapping (mod+ArrowLeft / mod+Home ARE caret motion — it must never be
          // stolen) and dispatches through the SAME command catalog the ribbon and
          // the DOM editor use. The clipboard seams give the hidden input sink the
          // three real flavours instead of its plain-text fallback.
          onShortcut: (ev) => servicesRef.current?.handleShortcut(ev) ?? false,
          readClipboard: () => servicesRef.current?.readClipboard() ?? null,
          onPasteFlavours: (f) => servicesRef.current?.onPasteFlavours(f) ?? false,
          onChange: (core) => publish(core),
          // A PRESS THE ENGINE REFUSED. Today that is a table cell / list item /
          // quote lowered out of an opaque markup island: painted text that carries
          // no addressable caret until the pdfz per-cell provenance + block
          // addressability work lands. The engine has already scrolled the existing
          // caret back into view; this makes the refusal legible instead of leaving
          // the reviewer to conclude the document is dead. `painted:false` (a press
          // on nothing) is deliberately NOT surfaced — there is nothing to explain.
          onPointRefused: (refusal) => {
            if (!refusal.painted) return;
            onRefusalRef.current?.(
              "This part of the document can't take a cursor yet (a table cell or " +
                "list item). Your cursor is still where it was — it hasn't moved.",
            );
          },
        });
        editorRef.current = editor;
        // THE SCALE — resolved through the ONE presentation policy every canvas
        // document surface shares (`canvasPresentation`), so the sheet a reviewer
        // EDITS and the sheet a reviewer READS are painted at the same size. A
        // no-op under today's policy; the call is the seam that keeps it single.
        applyCanvasScale(mount, editor, zoom);
        publish(editor.core, true); // seed the probe — never a "document changed"
        const fonts = (globalThis as { document?: { fonts?: FontFaceSet } }).document?.fonts;
        if (fonts?.ready) void fonts.ready.then(() => !disposed && editor?.repaint());
        // Spend a `focus()` the host asked for while the wasm was still loading
        // (see `pendingFocusRef`). The surface knows that "focused" means its
        // offscreen `HiddenInputSink`, so this is the same delegation the handle
        // does — just deferred to the first moment it can succeed.
        if (pendingFocusRef.current) {
          pendingFocusRef.current = false;
          editor.focus();
        }
        setLoad({ status: "ready" });
      } catch (err) {
        if (disposed) return;
        setLoad({ status: "error", message: err instanceof Error ? err.message : String(err) });
      }
    })();

    return () => {
      disposed = true;
      editor?.destroy();
      editorRef.current = null;
    };
  }, [doc, zoom, dpr, collectHighlightRects]);

  // The imperative surface — the SAME shape as the DOM `<ElaborateEditor>` so the
  // ribbon + Save consume it unchanged, PLUS the `capabilities` declaration that
  // tells the toolbar which of those verbs this surface actually performs (the
  // DOM editor performs all of them; the canvas does not — see canvasCapabilities).
  useImperativeHandle(
    ref,
    (): GuardedElaborateEditorHandle => ({
      // ── The surface's own capability statement (subtracts, never fakes) ───────
      // The ribbon's gate reads the wasm export table, which advertises verbs this
      // surface stubs. Publishing the denial here is what stops Insert / List /
      // Table rendering as enabled-but-dead buttons over the canvas.
      capabilities: CANVAS_CAPABILITIES,
      // ── REAL (C1 + C2) ────────────────────────────────────────────────────────
      // toHtml now serializes the STYLED overrides (inline marks persist through
      // Save); falls back to the plain handle render before the layer exists.
      toHtml: () => formatLayerRef.current?.toHtml() ?? liveHandleRef.current.toHtml(),
      // ── THE SAVE REFUSAL PREDICATE (P0 2026-07-28) ──────────────────────────
      // `toHtml()` stays PURE — the word-count bar re-reads it on every tick, so a
      // throw there would take the workbench down. The host asks this ONCE, at the
      // moment it is about to POST, and refuses the write when it is non-empty.
      // Returns `[]` when the layer is not mounted (nothing has been serialized,
      // so nothing can have been destroyed) — never a fabricated verdict.
      structuralLossOnSave: () => formatLayerRef.current?.structuralLossOnSave() ?? [],
      get root() {
        return mountRef.current;
      },
      // ── REAL: the surface's own SELECTION truth ──────────────────────────────
      // `EditorToolbarMount` gates every `requires: 'selection'` command on
      // `editorHandle.hasSelection?.() ?? false`. Leaving this unimplemented did
      // not make the gate fall back to the DOM (it deliberately never does) — it
      // made the gate answer `false` for the lifetime of the mount, so Insert ▸
      // Link was permanently dead on the paginated surface while the reviewer's
      // selection was painted on screen.
      //
      // A canvas has no DOM selection to consult: the truth is the wasm core's
      // `{anchor,focus}` IR coordinate pair. `canvasHasSelection` is the shared
      // upstream derivation of exactly that (RULE ZERO — the predicate lives on
      // the handle contract in @norkron/web-elaborate-editor so both surfaces
      // agree on what "there is a selection" means), and an unmounted core is
      // FALSE: no selection to act on.
      hasSelection: () => canvasHasSelection(editorRef.current?.core.getState().selection),
      // ── REAL: the surface's own FOCUS HANDBACK ───────────────────────────────
      // The other half of web-elaborate-editor FIX-3A. The toolbar's `after()`
      // calls `editorHandle.focus?.()` ONLY while `document.activeElement` is
      // inside `.ela-toolbar` — the keyboard-activation and popover paths, where
      // there is no `mousedown` to preventDefault. Leaving it unimplemented has
      // the same shape as the `hasSelection` defect above: the optional call
      // resolves to `undefined` and silently no-ops FOREVER, so ⌘Z after a ribbon
      // command goes to the button instead of the document. Measured live on
      // 2026-07-27: after clicking ribbon Bold, `document.activeElement` was
      // `BUTTON[Bold (⌘B)]` and the undo keystroke reached nothing.
      //
      // Delegating to the SURFACE (not to `mountRef`) is the point: the canvas
      // knows that "focused" means its offscreen `HiddenInputSink` textarea, not
      // its container — `canvas-surface.ts` binds `keydown` on the sink in EDIT
      // interactivity precisely so IME, autocorrect and assistive tech work. The
      // original bug was the host GUESSING with a `.docz-canvas` querySelector,
      // a class only the DOM controller stamps; asking the surface is what stops
      // the next surface repeating it. An unmounted core is a silent no-op, never
      // a throw — a toolbar command that races an unmount must not take the app
      // down.
      //
      // AND: a request that arrives BEFORE the wasm surface exists is LATCHED,
      // not dropped (see `pendingFocusRef`) — the mount effect spends it the
      // instant the surface is ready. Without that, "focus the editor when the
      // reviewer enters edit mode" is unimplementable from outside: the only tick
      // the host can act on is the one where the handle appears, which on a cold
      // load is always earlier than the one where the sink exists.
      focus: () => {
        const surface = editorRef.current;
        if (!surface) {
          pendingFocusRef.current = true;
          return;
        }
        pendingFocusRef.current = false;
        surface.focus();
      },
      // ── REAL (C3): comment anchoring on the canvas surface ──────────────────
      // The canvas Selection is INTERNAL (block-relative byte offsets), not a DOM
      // Range — so we build the selector straight from the caret's native offsets
      // (cleaner than the DOM path, which must invert geometry). The `Range`
      // parameter of the shared handle contract is vestigial here and IGNORED: a
      // host asks for a selector with NO argument ("your own live selection"),
      // which is the only form that can work on a surface with no DOM selection.
      buildSelector: (_range?: Range): AnchorSelector | null => selectorNow(),
      // Why the live selection could NOT be anchored durably, or `null` when it
      // can. The composer opens EITHER WAY (owner requirement 2026-07-29: "the
      // comment popup must show regardless of the selection") — this is the string
      // it shows instead of pretending. Only a surface can answer it, because only
      // a surface knows which BLOCK KINDS its selection covers.
      anchorRefusal: (): string | null => {
        const core = editorRef.current?.core;
        if (!core) return null;
        const slices = core.selectionSlices();
        if (slices.length === 0) return null;
        return anchorRefusal(canvasView(), slices, selectorNow());
      },
      // Where the live selection is PAINTED, in viewport coordinates — the anchor
      // a host positions the comment composer against. The engine owns the
      // projection (`viewportRectFor` maps page-space rects through the live page
      // transforms); this never touches the DOM selection, which on a canvas is
      // permanently collapsed and would place the composer at the origin.
      // `null` = no ranged selection, or the surface has not mounted.
      selectionViewportRect: (): DOMRect | null => {
        const editor = editorRef.current;
        const core = editor?.core;
        if (!editor || !core) return null;
        const rects = core.selectionRects();
        if (rects.length === 0) return null;
        return editor.viewportRectFor(rects);
      },
      // Insert the comment Mark into the docz IR over the canvas selection (real
      // `applyCommentMark` verb via the format layer), record the durable anchor,
      // and paint the highlight band. Save (`toHtml`) round-trips the mark.
      placeCommentMark: (commentId: string, _range?: Range): BlockPatch | null => {
        if (!commentId) return null;
        const layer = formatLayerRef.current;
        const editor = editorRef.current;
        if (!layer || !editor) return null;
        // The DURABLE anchor is named BEFORE the IR is touched, and it describes
        // the WHOLE range — including any block that then declines the Mark. The
        // anchor lives in `el.comment_anchors`, not in the IR, so a partial Mark
        // placement must not shrink what the comment claims to be about.
        const selector = selectorNow();
        if (!selector) return null;
        const placed = layer.placeComment(commentId); // reads the live canvas selection
        if (!placed || placed.spans.length === 0) return null;
        const handle = liveHandleRef.current;
        commentAnchorsRef.current.set(commentId, bandsOfSelector(selector));
        // A comment is a REVIEW ACT, not a text edit: drop both history stacks so
        // no undo can rewind past it. The save payload carries no CAS token and the
        // server rebases every open anchor against the saved body, so undoing past a
        // comment and saving would classify that anchor ORPHANED — an irreversible
        // review-state transition no client redo could repair.
        servicesRef.current?.historyBarrier();
        editor.setHighlights(collectHighlightRects()); // paint the new band now
        return {
          blockId: selector.blockId,
          baseVersionId: handle.baseVersionId,
          addComment: { commentId, selector },
        };
      },
      // Map a stored selector back to the canvas: re-locate its quote (exact at the
      // stored offsets, or shifted to where it now sits), register EVERY band and
      // paint them. A server/wasm {@link RebaseOutcome} (when supplied) OWNS the
      // 4-state lifecycle incl. `repaired` + the resolved range (rebase itself is a
      // COMMIT-time server concern — brief §6/§7). `spans` is empty: the canvas has
      // no per-span DOM nodes — the painted band IS the hit.
      //
      // ⛔ A CROSS-BLOCK QUOTE CONTAINS A NEWLINE, so it can never be found inside
      // ONE block's text. Routing it to `locateSelectorInText` (as this did) meant
      // every multi-block comment came back ORPHANED on the next reload and had its
      // highlight deleted — a worse failure than the one being fixed, because the
      // comment survived in the database while its anchor silently did not.
      // `bandsFromSelector` dispatches to the upstream cross-block locator, which
      // re-LOCATES the quote's per-block parts instead of trusting stored ids.
      resolveAnchor: (selector, commentId, outcome): AnchorHit => {
        const core = editorRef.current?.core;
        if (!core) return { state: "orphaned", commentId, spans: [] };
        const view = canvasView();
        const orphan = (): AnchorHit => {
          commentAnchorsRef.current.delete(commentId);
          editorRef.current?.setHighlights(collectHighlightRects());
          return { state: "orphaned", commentId, spans: [] };
        };
        if (outcome?.state === "orphaned") return orphan();

        const located = bandsFromSelector(view, selector);
        // The SERVER's verdict wins when it has one; the client locator only ever
        // supplies geometry in that case (and the selector's own decomposition is
        // the fallback when the quote has since moved out from under it).
        let state: AnchorHitState;
        let bands: readonly CommentBand[];
        if (outcome) {
          if (!outcome.range && !located) return orphan();
          state = outcome.state;
          bands =
            located?.bands ??
            bandsOfSelector({ ...selector, start: outcome.range!.start, end: outcome.range!.end });
        } else {
          if (!located) return orphan();
          state = located.state;
          bands = located.bands;
        }
        commentAnchorsRef.current.set(commentId, bands);
        editorRef.current?.setHighlights(collectHighlightRects());
        return { state, commentId, spans: [] };
      },
      // Rebase stays a thin stub: the fuzzy/quote-hash decision + audit event are
      // docz/server COMMIT-time concerns (brief §6). Returning null routes the host
      // to its server-side rebase — never a guessed client re-anchor. TODO(docz-FR).
      rebaseAnchor: () => null,
      // Re-paint the comment bands (the persistent highlight IS the anchor's
      // on-canvas presence). Canvas has no DOM spans → returns []. `state` is
      // advisory only for now (no distinct bloom hue on canvas yet).
      highlightAnchor: (): HTMLElement[] => {
        editorRef.current?.setHighlights(collectHighlightRects());
        return [];
      },
      // ── STUBS: proposals (out of scope — human-approval agent edits) ────────
      showProposal: () => false,
      acceptProposal: () => null,
      rejectProposal: () => null,
      // ── REAL (C2): out-of-band text edit (programmatic / agent proposal) ──────
      applyTextEdit: (edit): BlockPatch | null => {
        const ok = formatLayerRef.current?.applyTextEdit(edit.blockId, edit) ?? false;
        return ok ? { blockId: edit.blockId } : null;
      },
      getInsertMenu: () => insertLayerRef.current?.getInsertMenu() ?? [],
      validateBlock: () => [],
      // ── REAL (C2): inline formatting marks — drive the Canvas via docz verbs ──
      toggleMark: (type): ToggleResult => formatLayerRef.current?.toggleMark(type) ?? NOOP_TOGGLE,
      setMark: (mark) => formatLayerRef.current?.setMark(mark) ?? null,
      clearFormatting: () => formatLayerRef.current?.clearFormatting() ?? null,
      changeCase: (mode) => formatLayerRef.current?.changeCase(mode) ?? null,
      // REFUSE any conversion the canvas cannot address. A block the engine gives
      // no per-line provenance to lays out with `block_ord = NONE`: no caret can
      // ever enter it again, so applying the conversion anyway paints a DEAD ZONE
      // the user cannot even click back into. Returning null makes the ribbon
      // report the failure honestly. The predicate is the engine's OWN
      // (`isCanvasAddressableKind`, web-page-layout/block-map) — never a second
      // kind list here. Today `code` is the kind it refuses (pdfz stamps code
      // lines but reports them as `RunKind.paragraph`, so nothing on the wire
      // NAMES them and no namespace can pair them); heading, quote and list item
      // are all genuinely addressable and convert for real.
      //
      // THE PROBE IS THE BLOCK BEING CONVERTED, NOT A SYNTHETIC ONE. This used to
      // ask `isCanvasAddressableKind({ text: "", kind })`. The predicate is
      // TEXT-SENSITIVE: a blockquote is addressable only while it maps 1:1 onto a
      // single paragraph ordinal, which `isSingleParagraphQuote` decides from the
      // text carrying no `\n`. Probing with `""` therefore answered "addressable"
      // for EVERY quote conversion — including one applied to a block whose text
      // does carry a newline, which is precisely the multi-ord shape the map
      // refuses to address. The gate existed to prevent that dead zone and, on the
      // one input that could produce it, waved it through. The target ordinal is
      // resolved the same way `canvasFormatLayer.setKind` resolves it, so the gate
      // and the verb can never disagree about which block they are talking about.
      setBlockKind: (kind, level) => {
        const core = editorRef.current?.core;
        const ord = core?.getState().selection.focus.blockOrd ?? 0;
        // The probe text comes from the CANVAS DOCUMENT VIEW — the coordinate the
        // ordinal is expressed in. Reading `handle.blocks[ord]` probed a different
        // block whenever the document carried an opaque `html` island, so the gate
        // could refuse an addressable block or wave through an unaddressable one.
        const view = core ? canvasDoczBlocks(core.getState().bridge) : null;
        const text = (view ?? liveHandleRef.current.blocks)[ord]?.text ?? "";
        if (!isCanvasAddressableKind({ text, kind })) return null;
        return formatLayerRef.current?.setBlockKind(kind, level) ?? null;
      },
      setParaProps: (patch) => formatLayerRef.current?.setParaProps(patch) ?? null,
      // ── REAL, BUT SESSION-ONLY: section/page decoration + margins ────────────
      // Merge the patch onto the live section decoration, then re-run relayout
      // (`editor.refresh()` re-frames the V2 header via the `sectionDecoration`
      // getter, and re-reads the margin box via the `config` getter) and repaint —
      // the page fill / border box / line-number gutter / margin box now paint.
      //
      // ⚠ NOT PERSISTED. This comment used to claim the returned decoration was
      // "the persisted transport". It is not, and never has been. `sectionDecoration`
      // lives ONLY in this ref: Save posts `toHtml()` (`handle.toHtml()` maps over
      // BLOCKS — `web-docz/src/handle.ts`), the server re-ingests those HTML bytes
      // into a flat block list (`html_ingest.zig`, `.pages = &.{}`), and the reload
      // transport (`models.zig` `DoczTransport`) carries no decoration field at all.
      // Sections cannot ride the block wire by construction (`docz` `verbs.zig`
      // classifies `.section => .not_transported`), so closing this needs a
      // document-envelope field on BOTH `SaveBody` and `DoczTransport` plus a
      // column — and a decision on whether page geometry participates in
      // `content_sha256` (it gates reviewer verdicts). Tracked in the FR beside
      // this file. Everything below paints correctly and is lost on reload.
      setPageDecoration: (patch: SectionDecorationPatch): SectionDecoration => {
        sectionDecorationRef.current = mergeSectionDecoration(
          sectionDecorationRef.current,
          patch,
        );
        editorRef.current?.refresh();
        return sectionDecorationRef.current;
      },
      pageDecoration: (): SectionDecoration => sectionDecorationRef.current,
      // ── REAL: structural inserts (table, horizontal rule) ───────────────────
      // A table lands as a real structured block after the caret's own block, with
      // an id minted so its cells are caret-addressable from the first click. An
      // IMAGE and a PAGE BREAK refuse OUT LOUD (measured upstream gaps — see
      // `canvasInsertLayer`), and their commands are additionally greyed out
      // through `capabilities.images` / `capabilities.pageBreak`.
      insertBlock: (block) => insertLayerRef.current?.insertBlock(block) ?? false,
      insertPageBreak: () => insertLayerRef.current?.insertPageBreak() ?? null,
      // ── REAL: tables — the contextual Table ribbon group ────────────────────
      // `inTable` is the ONE gate the Table TAB's existence hangs on
      // (`CONTEXTUAL_TABS = { table: 'inTable' }` upstream). It used to be the
      // literal `false`, so the tab could never render on the canvas — the owner's
      // P0. It now answers from the caret's real geometry, so it is true for EVERY
      // table in the document, including the ones that arrived from the server.
      table: tableLayerRef.current?.verbs ?? NO_EDITOR_TABLE,
      inTable: () => tableLayerRef.current?.inTable() ?? false,
      tableSelection: () => tableLayerRef.current?.tableSelection() ?? null,
      hasCellRange: () => tableLayerRef.current?.hasCellRange() ?? false,
      tableHeaderOn: () => tableLayerRef.current?.tableHeaderOn() ?? false,
      // ── REAL: lists & numbering — the six docz list verbs, folded BLOCK-scoped ─
      // Every verb runs over EVERY block the selection touches
      // (`applyAcrossBlocks`), never over `selection.focus.blockOrd` alone: a list
      // command is block-structural over a RANGE, so resolving one ordinal is the
      // same defect cross-block comments had before `placeComment` was folded.
      // `null` is an honest decline — no editor, no list verbs in the loaded
      // `docz.wasm`, a read-only bridge, or every touched block refusing.
      setListStyle: (style) => listLayerRef.current?.setListStyle(style) ?? null,
      setListItemLevel: (delta) => listLayerRef.current?.setListItemLevel(delta) ?? null,
      restartList: (start) => listLayerRef.current?.restartList(start) ?? null,
      toggleTaskList: () => listLayerRef.current?.toggleTaskList() ?? null,
      setBulletGlyph: (glyph) => listLayerRef.current?.setBulletGlyph(glyph) ?? null,
      // ── REAL (C2): active format read off the caret's docz fragment ─────────
      activeFormat: (): ActiveFormat =>
        formatLayerRef.current?.activeFormat() ?? EMPTY_ACTIVE_FORMAT,
      // ── REAL: history / clipboard / painter (see `canvasEditorServices`) ────
      // The undo unit is a docz IR snapshot held in `EditorCore`'s bounded ring
      // (web-docz's shared `SnapshotHistory`) — never a TS shadow document.
      undo: () => servicesRef.current?.undo() ?? false,
      redo: () => servicesRef.current?.redo() ?? false,
      canUndo: () => servicesRef.current?.canUndo() ?? false,
      canRedo: () => servicesRef.current?.canRedo() ?? false,
      historyBarrier: () => servicesRef.current?.historyBarrier(),
      cut: () => servicesRef.current?.cut(),
      copy: () => servicesRef.current?.copy(),
      paste: () => servicesRef.current?.paste(),
      pasteFromSystem: (): Promise<SystemPasteResult> =>
        servicesRef.current?.pasteFromSystem() ?? Promise.resolve("unsupported"),
      canPaste: () => servicesRef.current?.canPaste() ?? false,
      formatPainter: (sticky?: boolean) =>
        servicesRef.current?.formatPainter(sticky) ?? false,
      painterArmed: () => servicesRef.current?.painterArmed() ?? false,
      handleShortcut: (ev: KeyboardEvent) =>
        servicesRef.current?.handleShortcut(ev) ?? false,
    }),
    [collectHighlightRects],
  );

  return (
    <div className={`pl-canvas-surface${className ? ` ${className}` : ""}`} data-testid={testId}>
      {load.status === "error" ? (
        <div className="pl-canvas-error" role="alert" data-testid="canvas-elaborate-error">
          The page view couldn&apos;t be loaded. Reload the page to try again.
        </div>
      ) : null}
      <div className="pl-canvas-pages" data-testid="canvas-elaborate-pages" ref={mountRef} />
      {/* Hidden live-IR probe — the round-trip witness for E2E (never rendered). */}
      <span ref={probeRef} data-testid="canvas-ir-plaintext" hidden />
    </div>
  );
});
